import { makeVar } from '@apollo/client'
import { Metadata } from '@deltek/specification-client/core/Models/metadata'
import {
    FontStyle,
    Justification,
    ListLevelNumberStyle,
} from 'models/projectStyle'
export interface IndentationStyles {
    left: number
    right: number
}

export type fontStyles = 'NONE' | 'BOLD' | 'ITALIC' | 'UNDERLINE'

export type justification = 'LEFT' | 'CENTER' | 'RIGHT' | 'JUSTIFY'

export interface FontSettings {
    fontColor: string
    fontFamily: string
    fontSize: number
    fontWeight?: number
    fontStyles: FontStyle[]
}

export interface PageMarginSettings {
    top: number
    left: number
    right: number
    bottom: number
}

//Project Style Guide Model
export interface ProjectStyle {
    projectId: string
    isActive: boolean
    name: string
    isDefaultStyle: boolean
    firmId: string
    id: string
    globalStyles: GlobalStyles
    masterFormatOutlineStyles: MasterFormatOutlineStyles
    sectionStyles: SectionStyles
    notesSpecifierStyles: NotesSpecifierStyles
    unitOfMeasureStyles: UnitOfMeasureStyles
    metadata: Partial<Metadata>
    overwriteMasterFormatStyles: boolean
    overwriteFontStyles: boolean
    isTemplateLocked: boolean
}

//Global Style Guide Settings
export interface GlobalStyles {
    font: FontSettings
    justification: Justification
    pageMargins: PageMarginSettings
    spaceAfter: number
    spaceBefore: number
}

//Master Format Outline Style Guide Settings
export interface ParagraphlevelStyles {
    numberStyle: ListLevelNumberStyle
    displayFormat: string
    fontColor: string
    fontFamily: string
    fontSize: number
    fontStyles: fontStyles[] | null
    spaceAfter: number
    spaceBefore: number
    tabSpaceToParagraphNumber: number
    tabSpaceToText: number
}

export interface MasterFormatOutlineStyles {
    article: ParagraphlevelStyles
    part: ParagraphlevelStyles
    paragraphLevel1: ParagraphlevelStyles
    paragraphLevel2: ParagraphlevelStyles
    paragraphLevel3: ParagraphlevelStyles
    paragraphLevel4: ParagraphlevelStyles
    paragraphLevel5: ParagraphlevelStyles
}
//Section Style Guide Settings
export interface SectionTitleStyles {
    font: FontSettings
    indentations: IndentationStyles
    justification: Justification
    setTextToUpper: boolean
    spaceAfter: number
    spaceBefore: number
    numberOfLines: number
    spaceBetweenLines: number
}
export interface EndOfSectionStyles {
    font: FontSettings
    indentations: IndentationStyles
    justification: Justification
    setTextToUpper: boolean
    spaceAfter: number
    spaceBefore: number
}
export interface SectionStyles {
    sectionTitleStyle: SectionTitleStyles
    endOfSectionStyle: EndOfSectionStyles
    omitSectionNumber: boolean
}

//Notes To Specificer Style Guide Settings
export interface NotesSpecifierStyles {
    font: FontSettings
    indentations: IndentationStyles
    justification: Justification
    setTextToUpper: boolean
    spaceAfter: number
    spaceBefore: number
}

//Unit Of Measure Style Guide Settings
export interface UnitOfMeasureStyles {
    imperial: FontSettings
    metric: FontSettings
}

export const defaultGlobalStyles: GlobalStyles = {
    font: {
        fontSize: 12,
        fontFamily: 'Arial',
        fontStyles: ['NONE'],
        fontColor: '#000000',
    },
    justification: 'JUSTIFY',
    pageMargins: {
        top: 1.0,
        bottom: 1.0,
        left: 1.25,
        right: 1.25,
    },
    spaceAfter: 0,
    spaceBefore: 0,
}

export const defaultMasterFormatOutlineStyles: MasterFormatOutlineStyles = {
    article: {
        numberStyle: 'ARABIC',
        displayFormat: '1.4',
        fontColor: '#000000',
        fontFamily: 'Arial',
        fontSize: 11,
        fontStyles: ['NONE'],
        spaceAfter: 0,
        spaceBefore: 24,
        tabSpaceToParagraphNumber: 0,
        tabSpaceToText: 1,
    },
    part: {
        numberStyle: 'ARABIC',
        displayFormat: 'PART',
        fontColor: '#000000',
        fontFamily: 'Arial',
        fontSize: 12,
        fontStyles: ['NONE'],
        spaceAfter: 0,
        spaceBefore: 24,
        tabSpaceToParagraphNumber: 0,
        tabSpaceToText: 0,
    },
    paragraphLevel1: {
        numberStyle: 'UPPERCASELETTER',
        displayFormat: '5.',
        fontColor: '#000000',
        fontFamily: 'Arial',
        fontSize: 11,
        fontStyles: ['NONE'],
        spaceAfter: 0,
        spaceBefore: 12,
        tabSpaceToParagraphNumber: 0.6,
        tabSpaceToText: 0.6,
    },
    paragraphLevel2: {
        numberStyle: 'ARABIC',
        displayFormat: '6.',
        fontColor: '#000000',
        fontFamily: 'Arial',
        fontSize: 11,
        fontStyles: ['NONE'],
        spaceAfter: 0,
        spaceBefore: 12,
        tabSpaceToParagraphNumber: 1,
        tabSpaceToText: 1,
    },
    paragraphLevel3: {
        numberStyle: 'LOWERCASELETTER',
        displayFormat: '7.',
        fontColor: '#000000',
        fontFamily: 'Arial',
        fontSize: 11,
        fontStyles: ['NONE'],
        spaceAfter: 0,
        spaceBefore: 12,
        tabSpaceToParagraphNumber: 1.4,
        tabSpaceToText: 1.4,
    },
    paragraphLevel4: {
        numberStyle: 'ARABIC',
        displayFormat: '8)',
        fontColor: '#000000',
        fontFamily: 'Arial',
        fontSize: 11,
        fontStyles: ['NONE'],
        spaceAfter: 0,
        spaceBefore: 12,
        tabSpaceToParagraphNumber: 1.8,
        tabSpaceToText: 1.8,
    },
    paragraphLevel5: {
        numberStyle: 'LOWERCASELETTER',
        displayFormat: '9)',
        fontColor: '#000000',
        fontFamily: 'Arial',
        fontSize: 11,
        fontStyles: ['NONE'],
        spaceAfter: 0,
        spaceBefore: 12,
        tabSpaceToParagraphNumber: 2.2,
        tabSpaceToText: 2.2,
    },
}

export const defaultSectionStyles: SectionStyles = {
    sectionTitleStyle: {
        font: {
            fontSize: 12,
            fontFamily: 'Arial',
            fontStyles: ['NONE'],
            fontColor: '#000000',
        },
        indentations: {
            left: 0,
            right: 0,
        },
        justification: 'JUSTIFY',
        setTextToUpper: false,
        spaceAfter: 0,
        spaceBefore: 12,
        numberOfLines: 1,
        spaceBetweenLines: 0,
    },
    endOfSectionStyle: {
        font: {
            fontSize: 11,
            fontFamily: 'Arial',
            fontStyles: ['NONE'],
            fontColor: '#000000',
        },
        indentations: {
            left: 0,
            right: 0,
        },
        justification: 'JUSTIFY',
        setTextToUpper: false,
        spaceAfter: 0,
        spaceBefore: 24,
    },
    omitSectionNumber: false,
}

export const defaultNotesSpecifierStyles: NotesSpecifierStyles = {
    font: {
        fontColor: '#000000',
        fontSize: 11,
        fontStyles: ['NONE'],
        fontFamily: 'Arial',
    },
    indentations: {
        left: 0,
        right: 0,
    },
    justification: 'LEFT',
    setTextToUpper: false,
    spaceAfter: 0,
    spaceBefore: 12,
}

export const defaultUnitOfMeasureStyles: UnitOfMeasureStyles = {
    imperial: {
        fontColor: '#FF0000',
        fontSize: 11,
        fontStyles: ['BOLD'],
        fontFamily: 'Arial',
    },
    metric: {
        fontColor: '#008080',
        fontSize: 11,
        fontStyles: ['BOLD'],
        fontFamily: 'Arial',
    },
}

const defaultProjectStyles: ProjectStyle = {
    projectId: '',
    isActive: false,
    name: '',
    isDefaultStyle: false,
    firmId: '',
    id: '',
    globalStyles: defaultGlobalStyles,
    masterFormatOutlineStyles: defaultMasterFormatOutlineStyles,
    sectionStyles: defaultSectionStyles,
    notesSpecifierStyles: defaultNotesSpecifierStyles,
    unitOfMeasureStyles: defaultUnitOfMeasureStyles,
    metadata: {},
    overwriteMasterFormatStyles: false,
    overwriteFontStyles: false,
    isTemplateLocked: false
}

const defaultIsStyleGuideDisabled: boolean = false

export const isStyleGuideDisabled = makeVar<boolean>(
    defaultIsStyleGuideDisabled
)

export const toggleIsStyleGuideDisabled = (value: boolean) => {
    isStyleGuideDisabled(value)
}

export const projectStyle = makeVar<ProjectStyle>({
    ...defaultProjectStyles,
})

export const setActiveProjectStyle = (value: ProjectStyle) => {
    projectStyle(value)
}

export const toggleIsGlobalFontToggled = (value: boolean) => {
    const projectStyles = projectStyle()

    projectStyle({
        ...projectStyles,
        overwriteMasterFormatStyles: value,
    })
}

export const toggleIsGlobalFontStyleToggled = (value: boolean) => {
    const projectStyles = projectStyle()

    projectStyle({
        ...projectStyles,
        overwriteFontStyles: value,
    })
}

export const updateGlobalSettings = (globalSettings: GlobalStyles) => {
    const projectStyles = projectStyle()

    projectStyle({
        ...projectStyles,
        globalStyles: globalSettings,
    })
}

export const updateSectionTitleSettings = (sectionSettings: SectionStyles) => {
    const projectStyles = projectStyle()

    projectStyle({
        ...projectStyles,
        sectionStyles: sectionSettings,
    })
}

export const toggleOmitSectionNumber = (value: boolean) => {
    const projectStyles = projectStyle()

    projectStyle({
        ...projectStyles,
        sectionStyles: {
            ...projectStyles.sectionStyles,
            omitSectionNumber: value,
        },
    })
}

export const updateNotesToSpecifierSettings = (
    notesSettings: NotesSpecifierStyles
) => {
    const projectStyles = projectStyle()

    projectStyle({
        ...projectStyles,
        notesSpecifierStyles: notesSettings,
    })
}

export const updateUnitOfMeasureSettings = (
    uomSettings: UnitOfMeasureStyles
) => {
    const projectStyles = projectStyle()

    projectStyle({
        ...projectStyles,
        unitOfMeasureStyles: uomSettings,
    })
}

export const updateMasterFormatOutlineSettings = (
    level: string,
    paragraphLevelSetting: ParagraphlevelStyles
) => {
    const projectStyles = projectStyle()

    projectStyle({
        ...projectStyles,
        masterFormatOutlineStyles: {
            ...projectStyles.masterFormatOutlineStyles,
            [level]: paragraphLevelSetting,
        },
    })
}

const defaultProjectStyleList: ProjectStyle[] = []

export const projectStyleList = makeVar<ProjectStyle[]>(defaultProjectStyleList)

export const setProjectStyleList = (value: ProjectStyle[]) => [
    projectStyleList(value),
]
